import { useEffect, useState } from "react";
import { Save } from "lucide-react";

import { useUpdateWorkerProfile } from "@/features/workers/model/useWorkerProfile";
import type { WorkerProfile } from "@/features/workers/types";
import { cn } from "@/shared/lib/cn";

interface Props {
  profile: WorkerProfile;
}

function profileRateAsString(profile: WorkerProfile): string {
  // 0 acts as "no floor" — render the input empty so the placeholder shows.
  if (!profile.min_hourly_rate) return "";
  const n = Number(profile.min_hourly_rate);
  return n > 0 ? String(n) : "";
}

export function MinRateEditor({ profile }: Props) {
  const update = useUpdateWorkerProfile();
  const [rate, setRate] = useState<string>(() => profileRateAsString(profile));
  const [error, setError] = useState<string | null>(null);

  // Reset when the upstream profile changes (e.g. after a save elsewhere).
  useEffect(() => {
    setRate(profileRateAsString(profile));
    setError(null);
  }, [profile.id, profile.min_hourly_rate]);

  const initial = profileRateAsString(profile);
  const isDirty = rate.trim() !== initial;

  const handleSave = async () => {
    setError(null);
    const trimmed = rate.trim();
    // Empty input clears the floor; the server treats null as "no minimum."
    if (trimmed === "") {
      await update.mutateAsync({ min_hourly_rate: "0" });
      return;
    }
    const value = Number(trimmed);
    if (!Number.isFinite(value) || value < 0 || value > 999) {
      setError("Enter a rate between 0 and 999.");
      return;
    }
    await update.mutateAsync({ min_hourly_rate: value.toFixed(2) });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="relative w-full max-w-[180px]">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
            $
          </span>
          <input
            type="number"
            inputMode="decimal"
            min={0}
            max={999}
            step="0.50"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            placeholder="20.00"
            className="w-full rounded-md border border-input bg-background py-2 pl-7 pr-12 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
            /hr
          </span>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={!isDirty || update.isPending}
          className={cn(
            "inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
            isDirty
              ? "bg-primary text-primary-foreground hover:opacity-90"
              : "border border-input text-muted-foreground",
            update.isPending && "opacity-50",
          )}
        >
          <Save className="h-3.5 w-3.5" />
          {update.isPending ? "Saving…" : "Save rate"}
        </button>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
      {update.isError && !error && (
        <p className="text-xs text-destructive">
          {(update.error as { response?: { data?: { detail?: string } } })?.response
            ?.data?.detail ?? "Could not save your rate."}
        </p>
      )}
    </div>
  );
}
